import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';
import { MenubarModule } from 'primeng/menubar';
import { MenuItem } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ButtonModule } from 'primeng/button';
import { PermissionsService, PermissionCheck } from '../../../services/permissions.service';
import { BillingTourCoordinatorService, BillingTourId } from './billing-tour-coordinator.service';

interface Action {
  readonly key: string;
  readonly label: string;
  readonly icon: string;
  readonly route: string;
  readonly blurb: string;
}

interface MenuGroup {
  readonly label: string;
  readonly icon: string;
  readonly actions: Action[];
}

interface TooltipConfig {
  readonly tooltipLabel: string;
  readonly tooltipPosition: 'top' | 'bottom' | 'left' | 'right';
  readonly showDelay: number;
}

const DEFAULT_ACTION_KEY = 'dashboard';
const BILLING_ROUTE = '/billing';
const ACTIVE_MENU_ITEM_CLASS = 'active-menu-item';

const TOOLTIP_CONFIG = {
  MAIN_ITEM: {
    tooltipPosition: 'bottom' as const,
    showDelay: 300,
  },
  SUB_ITEM: {
    tooltipPosition: 'right' as const,
    showDelay: 200,
  },
} as const;

@Component({
  selector: 'app-billing-wizard',
  standalone: true,
  imports: [CommonModule, RouterOutlet, MenubarModule, TooltipModule, OverlayPanelModule, ButtonModule],
  templateUrl: './billing-wizard.component.html',
  styleUrls: ['./billing-wizard.component.scss'],
})
export class BillingWizardComponent implements OnInit, OnDestroy {
  private readonly permissionsService = inject(PermissionsService);
  private readonly tourCoordinator = inject(BillingTourCoordinatorService);

  protected readonly tourOptions = [
    { id: 'dashboard' as BillingTourId, label: 'Dashboard', icon: 'pi pi-home', route: '/billing/dashboard' },
    { id: 'reconcile' as BillingTourId, label: 'NS ↔ Rev.io Reconcile', icon: 'pi pi-arrows-h', route: '/billing/reconcile' },
    { id: 'onboarding' as BillingTourId, label: 'Client Onboarding', icon: 'pi pi-user', route: '/billing/client-onboarding' },
    { id: 'mappings' as BillingTourId, label: 'Rev.io Mappings', icon: 'pi pi-link', route: '/billing/settings/ns-revio-mappings' },
    { id: 'queries' as BillingTourId, label: 'Rev.io Queries', icon: 'pi pi-search', route: '/billing/customers' },
    { id: 'non-billable' as BillingTourId, label: 'Non-Billable Management', icon: 'pi pi-ban', route: '/billing/non-billable' },
  ] as const;

  permissions: PermissionCheck = {
    canAdd: false,
    canEdit: false,
    canDelete: false,
    canView: false,
  };

  menuItems: MenuItem[] = [];
  activeKey: string | null = null;

  private readonly dashboard: Action = {
    key: 'dashboard',
    label: 'Dashboard',
    icon: 'pi pi-home',
    route: '/billing/dashboard',
    blurb: 'KPIs & quick links for billing.',
  };

  private readonly revioOperations: Action[] = [
    {
      key: 'reconcile',
      label: 'NS ↔ Rev.io',
      icon: 'pi pi-arrows-h',
      route: '/billing/reconcile',
      blurb: 'Compare Live/Snapshot vs Rev.io and sync quantities.',
    },
    {
      key: 'client-onboarding',
      label: 'Client Onboarding',
      icon: 'pi pi-user',
      route: '/billing/client-onboarding',
      blurb: 'Client data to be provided into the Rev.io platform',
    },
  ];

  private readonly utilities: Action[] = [
    {
      key: 'non-billable',
      label: 'Non-Billable',
      icon: 'pi pi-ban',
      route: '/billing/non-billable',
      blurb: 'Manage domain + site + service code combinations excluded from billing.',
    },
    {
      key: 'hosted-snapshots',
      label: 'Hosted Snapshots',
      icon: 'pi pi-clone',
      route: '/billing/snapshots',
      blurb: 'Saved snapshots, comparisons & CSV exports.',
    },
    {
      key: 'hosted-summary',
      label: 'Hosted Summary',
      icon: 'pi pi-chart-bar',
      route: '/billing/hosted-summary',
      blurb: 'Totals by site, scope & service type.',
    },
    {
      key: 'trunking-summary',
      label: 'Trunking Summary',
      icon: 'pi pi-server',
      route: '/billing/trunking-summary',
      blurb: 'Channels and TN metrics per domain.',
    },
    {
      key: 'fax-audit',
      label: 'Fax Audit',
      icon: 'pi pi-print',
      route: '/billing/fax-audit',
      blurb: 'Accounts & client types by domain.',
    },
  ];

  private readonly settingsActions: Action[] = [
    {
      key: 'ns-revio-mappings',
      label: 'Rev.io Customer Mappings',
      icon: 'pi pi-link',
      route: '/billing/settings/ns-revio-mappings',
      blurb: 'Manage domain to Rev.io customer mappings.',
    },
  ];

  private readonly revioQueries: Action[] = [
    {
      key: 'providers',
      label: 'Providers',
      icon: 'pi pi-building',
      route: '/billing/providers',
      blurb: 'Query and search providers.',
    },
    {
      key: 'services',
      label: 'Services',
      icon: 'pi pi-cog',
      route: '/billing/services',
      blurb: 'Query and search services.',
    },
    {
      key: 'service-types',
      label: 'Service Types',
      icon: 'pi pi-tags',
      route: '/billing/service-types',
      blurb: 'Query and search service types.',
    },
    {
      key: 'product-types',
      label: 'Product Types',
      icon: 'pi pi-box',
      route: '/billing/product-types',
      blurb: 'Query and search product types.',
    },
    {
      key: 'products',
      label: 'Products',
      icon: 'pi pi-shopping-cart',
      route: '/billing/products',
      blurb: 'Query and search products.',
    },
    {
      key: 'customers',
      label: 'Customers',
      icon: 'pi pi-users',
      route: '/billing/customers',
      blurb: 'Query and search customers.',
    },
  ];

  private readonly menuGroups: MenuGroup[] = [
    {
      label: 'Rev.io Operations',
      icon: 'pi pi-star-fill',
      actions: this.revioOperations,
    },
    {
      label: 'Rev.io Queries',
      icon: 'pi pi-search',
      actions: this.revioQueries,
    },
    {
      label: 'NS Operations',
      icon: 'pi pi-cog',
      actions: this.utilities,
    },
    {
      label: 'Settings',
      icon: 'pi pi-cog',
      actions: this.settingsActions,
    },
  ];

  private readonly destroy$ = new Subject<void>();

  constructor(private readonly router: Router) {}

  get actions(): Action[] {
    return [this.dashboard, ...this.revioOperations, ...this.utilities,       ...this.revioQueries,
      ...this.settingsActions];
  }

  async ngOnInit(): Promise<void> {
    await this.loadPermissions();
    this.syncWithCurrentUrl();
    this.setupRouterSubscription();
    this.buildMenuItems();
  }

  private async loadPermissions(): Promise<void> {
    this.permissions = await this.permissionsService.checkEntityPermissions('billing');
  }

  private syncWithCurrentUrl(): void {
    this.syncWithUrl(this.router.url);
  }

  private buildMenuItems(): void {
    const dashboardItem = this.createDashboardMenuItem();
    const groupItems = this.menuGroups.map((group) => this.createMenuGroupItem(group));

    this.menuItems = [dashboardItem, ...groupItems];
  }

  private createDashboardMenuItem(): MenuItem {
    return {
      label: this.dashboard.label,
      icon: this.dashboard.icon,
      command: () => this.onSelect(this.dashboard),
      tooltipOptions: this.createTooltipOptions(this.dashboard.blurb, TOOLTIP_CONFIG.MAIN_ITEM),
    };
  }

  private createMenuGroupItem(group: MenuGroup): MenuItem {
    return {
      label: group.label,
      icon: group.icon,
      items: group.actions.map((action) => this.createSubMenuItem(action)),
    };
  }

  private createSubMenuItem(action: Action): MenuItem {
    return {
      label: action.label,
      icon: action.icon,
      command: () => this.onSelect(action),
      tooltipOptions: this.createTooltipOptions(action.blurb, TOOLTIP_CONFIG.SUB_ITEM),
      data: { blurb: action.blurb },
    };
  }

  private createTooltipOptions(
    label: string,
    config: typeof TOOLTIP_CONFIG.MAIN_ITEM | typeof TOOLTIP_CONFIG.SUB_ITEM
  ): TooltipConfig {
    return {
      tooltipLabel: label,
      tooltipPosition: config.tooltipPosition,
      showDelay: config.showDelay,
    };
  }

  private setupRouterSubscription(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        this.syncWithUrl(event.urlAfterRedirects);
      });
  }

  private syncWithUrl(url: string): void {
    const normalizedUrl = this.normalizeUrl(url);
    const action = this.findActionByUrl(normalizedUrl);
    this.activeKey = action?.key ?? DEFAULT_ACTION_KEY;
    this.updateMenuItemsActiveState();
  }

  private normalizeUrl(url: string): string {
    return url.replace(/\/$/, '');
  }

  private findActionByUrl(url: string): Action | undefined {
    const exactMatch = this.actions.find((action) => url === action.route);
    if (exactMatch) {
      return exactMatch;
    }

    const prefixMatch = this.findActionByPrefix(url);
    if (prefixMatch) {
      return prefixMatch;
    }

    if (this.isBillingRoute(url)) {
      return this.dashboard;
    }

    return undefined;
  }

  private findActionByPrefix(url: string): Action | undefined {
    const sortedActions = [...this.actions].sort(
      (a, b) => b.route.length - a.route.length
    );
    return sortedActions.find((action) => url.startsWith(`${action.route}/`));
  }

  private isBillingRoute(url: string): boolean {
    return url === BILLING_ROUTE || url.endsWith(BILLING_ROUTE);
  }

  onSelect(action: Action): void {
    if (this.isActive(action)) {
      return;
    }

    this.activeKey = action.key;
    this.updateMenuItemsActiveState();
    this.router.navigateByUrl(action.route);
  }

  isActive(action: Action): boolean {
    return this.activeKey === action.key;
  }

  private updateMenuItemsActiveState(): void {
    this.menuItems.forEach((item) => {
      if (item.command) {
        item.styleClass = this.isActive(this.dashboard)
          ? ACTIVE_MENU_ITEM_CLASS
          : '';
      }

      if (item.items) {
        this.updateSubMenuItemsActiveState(item.items);
      }
    });
  }

  private updateSubMenuItemsActiveState(subItems: MenuItem[]): void {
    subItems.forEach((subItem) => {
      const action = this.findActionByLabel(subItem.label || '');
      if (action) {
        subItem.styleClass = this.isActive(action) ? ACTIVE_MENU_ITEM_CLASS : '';
      }
    });
  }

  private findActionByLabel(label: string): Action | undefined {
    return this.actions.find((action) => action.label === label);
  }

  protected launchTour(option: typeof this.tourOptions[number]): void {
    this.router.navigateByUrl(option.route).then(() => {
      setTimeout(() => this.tourCoordinator.requestTour(option.id), 500);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
